import React, { forwardRef, ReactNode, useCallback } from 'react'
import {
  CircularProgress,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'

import Toggle from '../../icons/Toggle'
import { Constant, Field, SelectEntity } from '../../types'
import { getTranslatedFieldName } from '../../utils/languageUtils'
import { ErrorTooltipProps } from '../ErrorTooltip'
import PuiInput from './PuiInput'
import SelectableMenuItem from './SelectableMenuItem'

const useStyles = makeStyles(
  theme => ({
    toggleIcon: {
      right: 0,
      color: theme.colors.toggleIcon,
      position: 'absolute',
      pointerEvents: 'none',
    },
    spinner: {
      position: 'relative',
      right: 25,
    },
    placeholder: {
      opacity: 0.5,
    },
    adornment: {
      marginLeft: 0,
    },
    colorPin: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      marginRight: theme.spacing(1),
      minWidth: 16,
    },
    menuItemWithPin: {
      paddingLeft: theme.spacing(1),
    },
    hidden: {
      display: 'none',
    },
    subheader: {
      color: theme.colors.inactiveBackground,
      fontWeight: 400,
      fontSize: theme.typography.pxToRem(12),
    },
  }),
  { name: 'PuiSelect' },
)

const DEFAULT_LABEL_PROP = 'name'
const DEFAULT_TRANSLATED_PROP = 'nameTranslation'

export type PuiSelectProps = SelectProps & {
  readonly Item?: typeof MenuItem | React.JSXElementConstructor<any>
  readonly TooltipProps?: Partial<ErrorTooltipProps>
  readonly field?: Field
  readonly isLoading?: boolean
  readonly items?: SelectEntity[]
  readonly keyProp?: Extract<keyof Constant, string>
  readonly renderEmpty?: boolean
  readonly renderSelectedValue?: (value: string) => ReactNode
  readonly selectItemProps?: object
  readonly valueProp?: keyof Constant | (keyof Constant)[]
}

const PuiSelect = forwardRef<unknown, PuiSelectProps>(function PuiSelect(
  {
    classes,
    disabled,
    items = [],
    keyProp = 'id',
    valueProp = DEFAULT_TRANSLATED_PROP,
    multiple,
    placeholder,
    renderEmpty = true,
    renderSelectedValue,
    selectItemProps,
    value: initialValue = multiple ? [] : '',
    isLoading = false,
    field,
    Item,
    TooltipProps,
    MenuProps,
    ...rest
  },
  ref,
) {
  const value = field?.value ?? initialValue
  const ownClasses = useStyles(rest)
  const valuePropParam = Array.isArray(valueProp) ? valueProp : [valueProp]

  // eslint-disable-next-line react/no-multi-comp
  const renderValueMultiple = (selected: string[]) => (
    <Typography noWrap color="secondary">
      {items
        .filter(item => selected.includes(item[keyProp]!))
        .map(item => getTranslatedFieldName(item))
        .join(', ')}
    </Typography>
  )

  const hasPins = items.some(item => item.pinColor)

  // MUI validates value based on SelectItem
  // So we construct a hidden SelectItem for each subitem
  const menuItems: Array<SelectEntity & { hide?: boolean }> = []
  items.forEach(item => {
    menuItems.push(item)
    item.subitems?.forEach(subitem => {
      menuItems.push({ ...subitem, hide: true })
    })
  })

  const isValidValue = menuItems.some(item =>
    multiple ? value.includes(item[keyProp]) : item[keyProp] === value,
  )

  const SelectItemComponent = Item || MenuItem

  const IconComponent = useCallback(
    () => (
      <InputAdornment
        classes={{ positionEnd: ownClasses.adornment }}
        position="end"
      >
        {isLoading && (
          <CircularProgress className={ownClasses.spinner} size={16} />
        )}
        {!disabled && <Toggle className={ownClasses.toggleIcon} />}
      </InputAdornment>
    ),
    [isLoading, disabled],
  )

  return (
    <PuiInput TooltipProps={TooltipProps} field={field}>
      <Select
        IconComponent={IconComponent}
        MenuProps={{ disableScrollLock: true, ...MenuProps }}
        classes={classes}
        disabled={disabled}
        displayEmpty={!multiple && renderEmpty}
        multiple={multiple}
        ref={ref}
        renderValue={
          multiple
            ? (renderValueMultiple as any)
            : (renderSelectedValue ?? undefined)
        }
        value={isValidValue ? value : multiple ? [] : ''}
        {...rest}
      >
        {!multiple && renderEmpty && (
          <SelectItemComponent
            classes={{ root: ownClasses.placeholder }}
            value=""
          >
            {placeholder || ' '}
          </SelectItemComponent>
        )}
        {menuItems.map(item => {
          const id = item[keyProp]
          const name = valuePropParam
            .map(prop => item[prop] || item[DEFAULT_LABEL_PROP])
            .join(' ')
          const displayName = R.is(String, item?.name)
            ? getTranslatedFieldName(item)
            : item?.name

          if (item.subheader) {
            return (
              <ListSubheader className={ownClasses.subheader} key={id}>
                {displayName}
              </ListSubheader>
            )
          }

          return multiple ? (
            <SelectableMenuItem
              key={id}
              name={name}
              selected={value.includes(id)}
              value={id}
            />
          ) : (
            <SelectItemComponent
              disabled={item.disabled}
              divider={item.divider}
              // Necessary to work with components that receives field or name prop
              // @ts-ignore
              field={field}
              key={id}
              name={displayName}
              subitems={item.subitems}
              value={id}
              {...(Item
                ? R.omit(
                  [
                    'key',
                    'name',
                    'nameTranslation',
                    'value',
                    'hide',
                    'pinColor',
                  ],
                  item,
                )
                : {})}
              classes={{
                root: classNames({
                  [ownClasses.menuItemWithPin]: hasPins,
                  [ownClasses.hidden]: item.hide,
                }),
              }}
              {...selectItemProps}
            >
              <Grid container item alignItems="center" wrap="nowrap">
                {hasPins && (
                  <Grid
                    bgcolor={item.pinColor}
                    className={ownClasses.colorPin}
                  />
                )}
                <Grid item overflow="hidden" textOverflow="ellipsis">
                  {displayName}
                </Grid>
              </Grid>
            </SelectItemComponent>
          )
        })}
      </Select>
    </PuiInput>
  )
})

export default PuiSelect
